// Asignar una observación a un proveedor por enlace, con el flujo de dos
// pasos de #0431 (piloto Arrow): 1) elegir de la nómina o dar de alta uno
// nuevo en línea; 2) revisar el destinatario —nombre y correo— antes de
// que salga un correo que no se puede deshacer, y confirmar.
//
// Desde ADR-0134 esto ya no delega (POST /delegate responde 410): corre
// sobre el hilo con POST /findings/:id/assign vía `useAssignFlow`, que
// mueve el estado, escribe el proveedor, emite el enlace `/r/b/<token>` y,
// en una reasignación, revoca el saliente en la misma transacción. El alta
// en línea viaja en el mismo acto (`newProvider`).
//
// Lo monta el panel de gestión del detalle (inline) y el drawer de la lista
// de facility. Es el flujo de las recetas SIN taxonomía (`assignFlavor`):
// las que la declaran asignan clasificando (AsignarForm del panel).
//
// El aviso al jefe de tienda ya no es un checkbox: lo decide la receta
// (`notifiesAssetParty` del estado de destino) y el servidor lo envía.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { observationDueDaysFor, type DerivedSeverity } from "@bitacorapro/core-engine";
import type { ProviderListItem } from "@bitacorapro/api-types";
import { Icon } from "./icons";
import { ProviderListBoundary } from "./skeleton-compositions/ProviderListSkel";
import { useRecipe } from "../lib/api/queries";
import { useAssignFlow, type AssignResult } from "../lib/postventa/useAssignFlow";
import { threadWords } from "../lib/postventa/words";
import { focusHeading } from "../lib/useDialogFocus";

/** Plazos ofrecidos, en días corridos; el de la receta se suma si no está. */
const PLAZOS = [3, 5, 7, 10, 15] as const;

// Las 16 regiones de Chile, en el orden del canvas (design pull, 2026-08-12).
const CHILE_REGIONS = [
  "Arica y Parinacota",
  "Tarapacá",
  "Antofagasta",
  "Atacama",
  "Coquimbo",
  "Valparaíso",
  "Metropolitana de Santiago",
  "O'Higgins",
  "Maule",
  "Ñuble",
  "Biobío",
  "La Araucanía",
  "Los Ríos",
  "Los Lagos",
  "Aysén",
  "Magallanes",
] as const;

type Step = "select" | "confirm";

type NewProviderDraft = {
  shortName: string;
  email: string;
  categories: string[];
  region: string;
};

const EMPTY_DRAFT: NewProviderDraft = { shortName: "", email: "", categories: [], region: "" };

function isValidEmail(v: string): boolean {
  // Mismo piso que el esquema del wire (emailSchema): el chequeo local sólo
  // evita un viaje por lo obviamente mal escrito; la validación real es
  // del servidor.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function dias(n: number): string {
  return `${n} ${n === 1 ? "día" : "días"}`;
}

export type ProviderAssignObservation = {
  id: string;
  /** Clave de severidad de la receta (para el plazo por defecto). */
  severity: string;
  effectiveSeverity?: DerivedSeverity | null | undefined;
  /** El proveedor vigente, si lo hay: preselecciona y avisa la reasignación. */
  providerId: string | null;
  /** «Tienda 34» — el objeto, para la nota prellenada. */
  assetTitle: string | null;
};

export function ProviderAssignForm({
  observation: o,
  notifiesAssetParty,
  onAssigned,
  onCancel,
  cancelLabel = "Cancelar",
}: {
  observation: ProviderAssignObservation;
  /** El estado de destino avisa al asset_party (receta). */
  notifiesAssetParty: boolean;
  onAssigned: (r: AssignResult) => void;
  onCancel?: () => void;
  cancelLabel?: string;
}) {
  const { data: recipe } = useRecipe();
  const words = threadWords(recipe);
  const flow = useAssignFlow({
    findingId: o.id,
    taxonomyVersion: null,
    failureTypeCode: null,
    currentSpecialtyCode: null,
    currentItemCode: null,
    aiSuggestedSpecialtyCode: null,
    aiSuggestedItemCode: null,
  });
  const providers = flow.nomina;
  const loaded = flow.providers.data !== undefined;

  const [step, setStep] = useState<Step>("select");
  const [creating, setCreating] = useState(false);
  const [selProvId, setSelProvId] = useState<string | null>(o.providerId);
  const [draft, setDraft] = useState<NewProviderDraft>(EMPTY_DRAFT);
  const [draftTouched, setDraftTouched] = useState(false);
  const [note, setNote] = useState(
    o.assetTitle ? `Por favor resuelvan la observación en ${o.assetTitle}.` : ""
  );
  const plazoDefault = useMemo(
    () =>
      recipe
        ? observationDueDaysFor(recipe, [
            { severity: o.severity, effectiveSeverity: o.effectiveSeverity ?? null },
          ])
        : null,
    [recipe, o.severity, o.effectiveSeverity]
  );
  const [plazo, setPlazo] = useState<number | null>(null);
  const plazoDias = plazo ?? plazoDefault;
  const plazos = useMemo(
    () =>
      [...new Set([...PLAZOS, ...(plazoDefault !== null ? [plazoDefault] : [])])].sort(
        (a, b) => a - b
      ),
    [plazoDefault]
  );
  const [error, setError] = useState<string | null>(null);

  // Sin nómina, el alta en línea es el único camino: se abre sola.
  useEffect(() => {
    if (loaded && providers.length === 0) setCreating(true);
  }, [loaded, providers.length]);

  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);
  const createHeadingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (step === "confirm") focusHeading(confirmHeadingRef);
    else if (creating) focusHeading(createHeadingRef);
  }, [step, creating]);

  const listRef = useRef<HTMLDivElement>(null);
  const ids = {
    list: useId(),
    note: useId(),
    plazo: useId(),
    plazoHint: useId(),
    name: useId(),
    email: useId(),
    emailHint: useId(),
    emailError: useId(),
    region: useId(),
    cats: useId(),
    catsHint: useId(),
    falta: useId(),
  };

  const selected: ProviderListItem | null = providers.find((p) => p.id === selProvId) ?? null;
  const emailValid = isValidEmail(draft.email.trim());
  const draftReady = draft.shortName.trim().length > 0 && emailValid && draft.categories.length > 0;
  const canReview = creating ? draftReady : selected !== null;
  const isReassignment = o.providerId !== null && (creating || o.providerId !== selProvId);
  const recipientName = creating ? draft.shortName.trim() : (selected?.shortName ?? null);
  const recipientEmail = creating ? draft.email.trim() : (selected?.contactEmail ?? null);

  const falta = canReview
    ? null
    : creating
      ? `Completa el nombre, un correo válido y al menos una categoría del ${words.assigneeLower}.`
      : `Elige un ${words.assigneeLower} para continuar.`;

  const categorySuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          providers.flatMap((p) => (p.specialty ? p.specialty.split(";").map((s) => s.trim()) : []))
        )
      ).filter(Boolean),
    [providers]
  );

  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (providers.length === 0) return;
    const delta =
      e.key === "ArrowDown" || e.key === "ArrowRight"
        ? 1
        : e.key === "ArrowUp" || e.key === "ArrowLeft"
          ? -1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    const i = providers.findIndex((p) => p.id === selProvId);
    const next = providers[(Math.max(i, 0) + delta + providers.length) % providers.length]!;
    setSelProvId(next.id);
    listRef.current?.querySelector<HTMLElement>(`[data-provider-id="${next.id}"]`)?.focus();
  }

  async function confirmar() {
    if (flow.pending) return;
    setError(null);
    try {
      const target = creating
        ? {
            newProvider: {
              shortName: draft.shortName.trim(),
              email: draft.email.trim(),
              // Una sola columna `specialty`: las categorías van unidas
              // por «; », como pide el propio campo del canvas.
              specialty: draft.categories.join("; "),
              ...(draft.region ? { region: draft.region } : {}),
            },
          }
        : { providerId: selProvId! };
      const res = await flow.submit(target, note.trim(), {
        ...(plazo !== null ? { plazoDias: plazo } : {}),
      });
      onAssigned(res);
    } catch (err) {
      // El paso de confirmación queda abierto para reintentar sin volver
      // a llenar el alta.
      setError(err instanceof Error ? err.message : "No pudimos guardar la asignación.");
    }
  }

  if (step === "confirm") {
    return (
      <>
        <h3 ref={confirmHeadingRef} tabIndex={-1} className="drawer__obs-title">
          Confirmar asignación
        </h3>
        <div className="field">
          <div className="cell-sub">Destinatario</div>
          <div className="prov-option__name">{recipientName ?? "—"}</div>
          <div className="cell-sub">{recipientEmail ?? "Sin correo registrado"}</div>
          {creating && (
            <div className="cell-sub">
              Se agrega a la nómina de {words.assigneePluralLower} al confirmar.
            </div>
          )}
        </div>
        {plazoDias !== null && (
          <div className="field">
            <div className="cell-sub">Plazo</div>
            <div>{dias(plazoDias)}</div>
          </div>
        )}
        {note.trim() && (
          <div className="field">
            <div className="cell-sub">Instrucciones</div>
            <div>{note.trim()}</div>
          </div>
        )}
        <p className="g-text g-text--meta">
          Recibirá un enlace con la observación y su evidencia; no necesita cuenta.
          {notifiesAssetParty
            ? ` También se le avisa ${words.elAssetParty.replace(/^el /, "al ")}, si la ${words.assetLower} tiene uno con cuenta.`
            : ""}
        </p>
        {isReassignment && (
          <p className="g-text g-text--meta">
            Esto reemplaza la asignación anterior: el enlace del {words.assigneeLower} anterior
            queda revocado.
          </p>
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        <div className="g-actions">
          <button
            type="button"
            className="btn btn--primary"
            aria-disabled={flow.pending}
            onClick={() => void confirmar()}
          >
            <Icon.Send s={14} />{" "}
            {flow.pending
              ? "Enviando…"
              : isReassignment
                ? "Confirmar reasignación"
                : "Confirmar y enviar"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setStep("select")}
            disabled={flow.pending}
          >
            Volver a editar
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {!creating ? (
        <div className="field">
          <div className="field__label" id={ids.list}>
            {words.assignee}
          </div>
          <ProviderListBoundary query={flow.providers} className="prov-list g-list">
            <div
              ref={listRef}
              className="prov-list g-list"
              role="radiogroup"
              aria-labelledby={ids.list}
              onKeyDown={onListKeyDown}
            >
              {providers.map((p, i) => {
                const checked = selProvId === p.id;
                const focusable = checked || (selProvId === null && i === 0);
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={focusable ? 0 : -1}
                    data-provider-id={p.id}
                    className={`prov-option${checked ? " prov-option--selected" : ""}`}
                    onClick={() => setSelProvId(p.id)}
                  >
                    <span className="prov-option__meta">
                      <span className="prov-option__name">
                        {p.shortName}
                        {o.providerId === p.id && (
                          <span className="prov-option__rec"> · asignado ahora</span>
                        )}
                      </span>
                      <span className="field__hint">
                        {[p.specialty, `${p.activeJobs} en curso`].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    {checked && (
                      <span className="prov-option__check" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </ProviderListBoundary>
          <div className="g-actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setDraft(EMPTY_DRAFT);
                setDraftTouched(false);
                setCreating(true);
              }}
            >
              <Icon.Plus s={12} /> Nuevo {words.assigneeLower}
            </button>
          </div>
        </div>
      ) : (
        <div className="prov-option--new-form">
          <h4 ref={createHeadingRef} tabIndex={-1} className="field__label">
            Nuevo {words.assigneeLower}
          </h4>
          {loaded && providers.length === 0 && (
            <p className="g-text">
              Todavía no tienes {words.assigneePluralLower} en la nómina. Agrega el primero:
              necesitas su nombre, su correo y lo que atiende.
            </p>
          )}
          <div className="field">
            <label className="field__label" htmlFor={ids.name}>
              Nombre
            </label>
            <input
              id={ids.name}
              className="input"
              value={draft.shortName}
              onChange={(e) => setDraft((d) => ({ ...d, shortName: e.target.value }))}
              placeholder="Ej. ClimAndes"
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor={ids.email}>
              Correo
            </label>
            <input
              id={ids.email}
              type="email"
              className="input"
              value={draft.email}
              onBlur={() => setDraftTouched(true)}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              aria-describedby={
                draftTouched && !emailValid && draft.email ? ids.emailError : ids.emailHint
              }
              aria-invalid={draftTouched && !emailValid && draft.email ? true : undefined}
              placeholder="contacto@proveedor.cl"
            />
            <p id={ids.emailHint} className="field__hint">
              A este correo le llega el enlace de la observación.
            </p>
            {draftTouched && !emailValid && draft.email && (
              <p id={ids.emailError} className="auth-error" role="alert">
                Ingresa un correo válido.
              </p>
            )}
          </div>
          <div className="field">
            <label className="field__label" htmlFor={ids.region}>
              Región
            </label>
            <select
              id={ids.region}
              className="select"
              value={draft.region}
              onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))}
            >
              <option value="">Selecciona una región</option>
              {CHILE_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <span className="field__label" id={ids.cats}>
              Categorías que atiende
            </span>
            <CategoriesMultiselect
              labelId={ids.cats}
              hintId={ids.catsHint}
              values={draft.categories}
              suggestions={categorySuggestions}
              onChange={(categories) => setDraft((d) => ({ ...d, categories }))}
            />
            <span className="field__hint" id={ids.catsHint}>
              Enter agrega la categoría escrita si no existe.
            </span>
          </div>
          {providers.length > 0 && (
            <div className="g-actions">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setCreating(false)}
              >
                ‹ Elegir de la nómina
              </button>
            </div>
          )}
        </div>
      )}

      {plazoDias !== null && (
        <div className="field">
          <label className="field__label" htmlFor={ids.plazo}>
            Plazo
          </label>
          <select
            id={ids.plazo}
            className="select"
            value={plazoDias}
            onChange={(e) => setPlazo(Number(e.target.value))}
            aria-describedby={ids.plazoHint}
          >
            {plazos.map((d) => (
              <option key={d} value={d}>
                {dias(d)}
              </option>
            ))}
          </select>
          {plazoDefault !== null && (
            <p id={ids.plazoHint} className="field__hint">
              Por defecto {dias(plazoDefault)} según la severidad
            </p>
          )}
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor={ids.note}>
          Instrucciones (opcional)
        </label>
        <textarea
          id={ids.note}
          className="textarea"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Contexto, accesos, horarios…"
        />
      </div>

      {falta && (
        <p className="field__hint" id={ids.falta}>
          {falta}
        </p>
      )}
      <div className="g-actions">
        <button
          type="button"
          className="btn btn--primary"
          aria-disabled={!canReview}
          aria-describedby={falta ? ids.falta : undefined}
          onClick={() => {
            if (canReview) setStep("confirm");
          }}
        >
          Revisar y enviar
        </button>
        {onCancel && (
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
        )}
      </div>
    </>
  );
}

// «Categorías que atiende» — tag-input combobox (design pull, 2026-08-12),
// patrón completo del combobox de WAI-ARIA APG: el input lleva
// role="combobox" + aria-expanded + aria-controls + aria-activedescendant,
// el menú es un role="listbox" real de role="option", y las flechas mueven
// aria-activedescendant sin sacar el foco del input. Enter agrega la
// sugerencia activa o, si nada calza, el texto escrito. Backspace con el
// input vacío quita la última etiqueta.
function CategoriesMultiselect({
  labelId,
  hintId,
  values,
  suggestions,
  onChange,
}: {
  labelId: string;
  hintId: string;
  values: string[];
  suggestions: string[];
  onChange: (values: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuId = `${labelId}-menu`;

  const filtered = suggestions.filter(
    (s) =>
      !values.includes(s) &&
      (inputValue.trim() === "" || s.toLowerCase().includes(inputValue.trim().toLowerCase()))
  );
  const exactMatch = filtered.some((s) => s.toLowerCase() === inputValue.trim().toLowerCase());
  const canAddNew =
    inputValue.trim().length > 0 && !exactMatch && !values.includes(inputValue.trim());
  const optionCount = filtered.length + (canAddNew ? 1 : 0);

  function addCategory(value: string) {
    const trimmed = value.trim();
    if (!trimmed || values.includes(trimmed)) return;
    onChange([...values, trimmed]);
    setInputValue("");
    setActiveIndex(-1);
    setMenuOpen(false);
  }

  function removeCategory(value: string) {
    onChange(values.filter((v) => v !== value));
    inputRef.current?.focus();
  }

  function activeOptionId(): string | undefined {
    if (activeIndex < 0) return undefined;
    return activeIndex < filtered.length ? `${menuId}-opt-${activeIndex}` : `${menuId}-opt-new`;
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMenuOpen(true);
      setActiveIndex((i) => (optionCount === 0 ? -1 : (i + 1) % optionCount));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMenuOpen(true);
      setActiveIndex((i) => (optionCount === 0 ? -1 : (i - 1 + optionCount) % optionCount));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        addCategory(filtered[activeIndex]!);
      } else if (inputValue.trim()) {
        addCategory(inputValue);
      }
    } else if (e.key === "Backspace" && inputValue === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    } else if (e.key === "Escape") {
      setMenuOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div className="multiselect" data-ms="">
      {values.length > 0 && (
        <div className="multiselect__tags" data-ms-tags="">
          {values.map((v) => (
            <span key={v} className="ms-tag">
              {v}
              <button
                type="button"
                className="ms-tag__x"
                onClick={() => removeCategory(v)}
                aria-label={`Quitar ${v}`}
              >
                <Icon.Close s={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        className="multiselect__input"
        role="combobox"
        aria-expanded={menuOpen && optionCount > 0}
        aria-autocomplete="list"
        aria-controls={menuId}
        aria-activedescendant={activeOptionId()}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        value={inputValue}
        placeholder="Filtrar o escribir para agregar…"
        onChange={(e) => {
          setInputValue(e.target.value);
          setMenuOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setMenuOpen(true)}
        onBlur={() => setMenuOpen(false)}
        onKeyDown={onKeyDown}
      />
      <div
        className="multiselect__menu"
        data-ms-menu=""
        id={menuId}
        role="listbox"
        hidden={!menuOpen || optionCount === 0}
      >
        {filtered.map((s, i) => (
          <button
            key={s}
            type="button"
            id={`${menuId}-opt-${i}`}
            role="option"
            aria-selected={i === activeIndex}
            className={`ms-option ${i === activeIndex ? "ms-option--active" : ""}`}
            // onMouseDown, no onClick: corre antes del onBlur del input que
            // cierra el menú, así el clic igual cae en la opción.
            onMouseDown={(e) => {
              e.preventDefault();
              addCategory(s);
            }}
          >
            {s}
          </button>
        ))}
        {canAddNew && (
          <button
            type="button"
            id={`${menuId}-opt-new`}
            role="option"
            aria-selected={activeIndex === filtered.length}
            className={`ms-option ms-option--new ${activeIndex === filtered.length ? "ms-option--active" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              addCategory(inputValue);
            }}
          >
            {inputValue.trim()}
            <span className="ms-option__hint">nueva</span>
          </button>
        )}
      </div>
    </div>
  );
}
